import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GeneratedCover } from '../components/ui/GeneratedCover';
import { ChevronRight, Zap, BookOpen, Smartphone } from 'lucide-react';
import { listBooks } from '../services/api';
import { GENRES } from '../services/genres';

export const Home: React.FC = () => {
  const [books, setBooks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await listBooks();
        setBooks(res.books || []);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const popularBooks = books.filter(b => b.is_popular);
  const newBooks = books.filter(b => b.is_new);

  return (
    <div className="pb-24">
      {/* Hero */}
      <section className="px-6 py-10 md:py-16 text-center animate-in fade-in duration-700">
        <h1 className="font-serif text-3xl md:text-5xl font-bold mb-4 leading-tight text-paper-text dark:text-night-text">
          Online Library of Case Files
        </h1>
        <p className="text-base md:text-lg opacity-70 mb-4 max-w-2xl mx-auto leading-relaxed">
          An open space for crime stories, investigations, and sharp fiction. Some files are personal, some are public, all of them are readable.
        </p>
        <p className="text-base md:text-lg opacity-70 mb-8 max-w-2xl mx-auto leading-relaxed">
          Start any case for free. If you want the full story, we send you a magic link. No passwords, no paywalls.
        </p>
        <Link to="/search" className="inline-block bg-brand-blue text-white px-8 py-3.5 rounded-full font-medium text-sm tracking-wide shadow-lg hover:shadow-xl hover:bg-brand-hover transition-all hover:-translate-y-0.5">
          Browse Case Files
        </Link>
      </section>

      {/* Genres */}
      <section className="mb-12 px-4 md:px-6">
        <h2 className="font-sans font-bold text-xl mb-1">Genres</h2>
        <p className="text-sm opacity-60 mb-4">Choose the shelf. The stories rarely stay inside it.</p>
        <div className="grid grid-flow-col auto-cols-[200px] gap-3 overflow-x-auto pb-4 no-scrollbar">
          {GENRES.map(g => (
            <Link
              key={g.slug}
              to={`/genre/${g.slug}`}
              className="snap-start shrink-0 h-32 rounded-2xl bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 transition-colors p-4 flex flex-col justify-between"
            >
              <span className="font-serif font-bold text-lg leading-tight line-clamp-2">{g.name}</span>
              <span className="text-[11px] uppercase opacity-60 font-bold tracking-wider leading-snug line-clamp-3">
                {g.desc}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Popular */}
      <section className="mb-12 pl-6">
        <div className="flex justify-between items-center pr-6 mb-2">
          <div>
            <h2 className="font-sans font-bold text-xl">Most Read</h2>
            <p className="text-xs opacity-60">If a lot of people read it, someone wishes they didn’t.</p>
          </div>
          <Link to="/search" className="text-brand-blue text-xs font-bold uppercase tracking-wider flex items-center hover:underline">
            See all <ChevronRight size={14} />
          </Link>
        </div>
        {loading ? (
          <div className="px-6 py-6 text-sm opacity-60">Loading...</div>
        ) : error ? (
          <div className="px-6 py-6 text-sm text-red-500">{error}</div>
        ) : (
          <div className="flex gap-5 overflow-x-auto pb-6 pr-6 no-scrollbar snap-x">
            {popularBooks.map(book => (
              <Link key={book.id} to={`/book/${book.id}`} className="snap-start shrink-0 w-36 flex flex-col gap-3 group">
                <GeneratedCover title={book.title} author={book.author} genre={book.genre} size="lg" className="shadow-lg group-hover:shadow-xl transition-shadow duration-300" />
                <div>
                  <h3 className="font-serif font-bold text-sm leading-tight line-clamp-2 group-hover:text-brand-blue transition-colors">{book.title}</h3>
                  <p className="text-xs opacity-60 mt-1">{book.author}</p>
                  <span className="inline-block mt-2 text-[10px] bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 px-1.5 py-0.5 rounded font-bold">First part free</span>
                </div>
              </Link>
            ))}
            {popularBooks.length === 0 && <div className="px-6 py-6 text-sm opacity-60">No case files yet. Silence doesn’t always mean nothing is happening.</div>}
          </div>
        )}
      </section>

      {/* New Releases */}
      <section className="px-6 mb-16">
        <div className="mb-2">
          <h2 className="font-sans font-bold text-xl">New Releases</h2>
          <p className="text-xs opacity-60">Fresh cases from readers and writers.</p>
        </div>
        {loading ? (
          <div className="text-sm opacity-60">Loading...</div>
        ) : error ? (
          <div className="text-sm text-red-500">{error}</div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {newBooks.map(book => (
              <Link key={book.id} to={`/book/${book.id}`} className="flex gap-5 items-start group p-3 -mx-3 rounded-xl hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
                <GeneratedCover title={book.title} author={book.author} size="sm" className="shrink-0 shadow-md" />
                <div className="flex-1 min-w-0 py-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] uppercase font-bold tracking-wider opacity-50">{book.genre}</span>
                  </div>
                  <h3 className="font-serif font-bold text-lg leading-tight mb-1 group-hover:text-brand-blue">{book.title}</h3>
                  <p className="text-sm opacity-70 mb-2">{book.author}</p>
                  <p className="text-xs opacity-50 line-clamp-2 leading-relaxed">{book.description}</p>
                </div>
              </Link>
            ))}
            {newBooks.length === 0 && <div className="text-sm opacity-60">No new case files yet. The stories are loading; reality is already live.</div>}
          </div>
        )}
      </section>

      {/* How It Works */}
      <section className="mx-6 mb-12">
        <h2 className="font-sans font-bold text-xl mb-6 text-center">How It Works</h2>
        <div className="grid gap-4">
          {/* Step 1 */}
          <div className="bg-paper-bg dark:bg-white/5 border border-black/5 dark:border-white/5 p-6 rounded-2xl flex items-start gap-4">
            <div className="p-3 bg-brand-blue/10 text-brand-blue rounded-full shrink-0">
              <BookOpen size={24} />
            </div>
            <div>
              <h3 className="font-bold mb-1">1. Read for Free</h3>
              <p className="text-sm opacity-70 leading-relaxed">Open a file and start reading. No account required.</p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="bg-paper-bg dark:bg-white/5 border border-black/5 dark:border-white/5 p-6 rounded-2xl flex items-start gap-4">
             <div className="p-3 bg-brand-blue/10 text-brand-blue rounded-full shrink-0">
               <Zap size={24} />
             </div>
             <div>
               <h3 className="font-bold mb-1">2. Unlock with a Magic Link</h3>
               <p className="text-sm opacity-70 leading-relaxed">Leave your email to get a one-click link to the full case. Passwords are for banks, not books.</p>
             </div>
          </div>

          {/* Step 3 */}
          <div className="bg-paper-bg dark:bg-white/5 border border-black/5 dark:border-white/5 p-6 rounded-2xl flex items-start gap-4">
             <div className="p-3 bg-brand-blue/10 text-brand-blue rounded-full shrink-0">
               <Smartphone size={24} />
             </div>
             <div>
               <h3 className="font-bold mb-1">3. Return Anytime</h3>
               <p className="text-sm opacity-70 leading-relaxed">We remember where you stopped, even if you don’t.</p>
             </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-10 border-t border-black/5 dark:border-white/5 text-center opacity-40 text-xs">
        <div className="flex justify-center gap-6 mb-4 font-medium">
          <Link to="/about">About</Link>
          <Link to="/manifesto">Manifesto</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/contact">Contact</Link>
        </div>
        <p>&copy; {new Date().getFullYear()} Readsy. All rights reserved.</p>
      </footer>
    </div>
  );
};
